import { useState } from "react";
import { ArrowUpDown, Check, ChevronDown, ChevronUp } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

export type SortField = "name" | "dateCreated" | "dateUpdated" | "storage" | "status";

/** 0 = the field's "primary" direction, 1 = its reverse. */
export type SortDirIndex = 0 | 1;

export type SortState = {
  field: SortField;
  dirIndex: SortDirIndex;
};

const FIELD_ORDER: SortField[] = ["name", "dateCreated", "dateUpdated", "storage", "status"];

const FIELD_LABELS: Record<SortField, string> = {
  name: "Name",
  dateCreated: "Date Created",
  dateUpdated: "Date Updated",
  storage: "Storage",
  status: "Status",
};

/**
 * Fields with real backing data. All five wired end-to-end 2026-07-12:
 *   dateUpdated + storage come from the aggregate join on GET /projects
 *   status uses projects.inactive_at (added in migration 0009)
 */
const FIELD_AVAILABLE: Record<SortField, boolean> = {
  name: true,
  dateCreated: true,
  dateUpdated: true,
  storage: true,
  status: true,
};

const DIRECTION_LABELS: Record<SortField, [string, string]> = {
  name: ["A → Z", "Z → A"],
  dateCreated: ["Newest → Oldest", "Oldest → Newest"],
  dateUpdated: ["Newest → Oldest", "Oldest → Newest"],
  storage: ["Largest → Smallest", "Smallest → Largest"],
  status: ["Active → Inactive", "Inactive → Active"],
};

type Props = {
  value: SortState;
  onChange: (next: SortState) => void;
};

export function ProjectSortMenu({ value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [fieldListOpen, setFieldListOpen] = useState(false);

  const fieldLabel = FIELD_LABELS[value.field];
  const directionLabel = DIRECTION_LABELS[value.field][value.dirIndex];

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setFieldListOpen(false);
      }}
    >
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-1.5 text-muted-foreground transition-colors hover:text-foreground">
          <ArrowUpDown className="h-3.5 w-3.5" strokeWidth={1.75} />
          Sorted by <span className="font-medium text-foreground">{fieldLabel}</span>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-64 p-2">
        <p className="px-1 pb-1.5 text-xs font-medium text-muted-foreground">Sort by</p>

        {/* Field select */}
        <button
          type="button"
          onClick={() => setFieldListOpen((v) => !v)}
          className="flex w-full items-center justify-between rounded-md bg-secondary px-2.5 py-2 text-sm font-medium text-foreground"
        >
          {fieldLabel}
          {fieldListOpen ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
          )}
        </button>

        {fieldListOpen && (
          <div className="mt-1 flex flex-col gap-0.5 rounded-md border border-border py-1">
            {FIELD_ORDER.map((field) => {
              const available = FIELD_AVAILABLE[field];
              const selected = field === value.field;
              return (
                <button
                  key={field}
                  type="button"
                  disabled={!available}
                  onClick={() => {
                    onChange({ field, dirIndex: 0 });
                    setFieldListOpen(false);
                  }}
                  className={cn(
                    "flex items-center justify-between px-2.5 py-1.5 text-left text-sm transition-colors",
                    available
                      ? "text-foreground hover:bg-secondary"
                      : "cursor-not-allowed text-muted-foreground/60"
                  )}
                >
                  <span>{FIELD_LABELS[field]}</span>
                  {selected && available && <Check className="h-3.5 w-3.5 text-primary" />}
                  {!available && <span className="text-xs italic">Not available yet</span>}
                </button>
              );
            })}
          </div>
        )}

        {/* Direction toggle */}
        <button
          type="button"
          onClick={() => onChange({ field: value.field, dirIndex: value.dirIndex === 0 ? 1 : 0 })}
          className="mt-1 flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        >
          <ArrowUpDown className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
          from {directionLabel}
        </button>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
